import { sections } from '@/mocks/audit'
const sectionOrder = [...Reflect.ownKeys(sections), 'additional-components', 'next-steps']

const assignment = {
  namespaced: true,
  state: {
    assignments: [],
    audits: [],
    currentAssignment: null,
    lastUpdated: null,
    fetchStart: null,
    fetchEnd: null,
    error: null
  },
  mutations: {
    SET_ASSIGNMENTS(state, assignments) {
      state.assignments = assignments
    },
    SET_AUDITS(state, audits) {
      state.audits = audits
    },
    ADD_ASSIGNMENTS(state, assignments) {
      if (Array.isArray(assignments)) {
        assignments = [assignments]
      }
      state.assignments = [...state.assignments, ...assignments]
    },
    SET_CURRENT_ASSIGNMENT(state, assignment) {
      state.currentAssignment = assignment
    },
    COMPLETE_ASSIGNMENT(state, assignment) {
      state.assignments = state.assignments
        .filter(a => a._id === assignment._id)
        .push(assignment)
    },
    ARCHIVE_ASSIGNMENT(state, id) {
      state.assignments = state.assignments.filter(a => a._id !== id)
    },
    FETCH_START(state) {
      state.fetchStart = Date.now()
    },
    FETCH_END(state) {
      state.fetchEnd = Date.now()
    },
    FETCH_ERROR(state, error) {
      state.error = error
    },
    UPLOAD_START(state) {
      state.fetchStart = Date.now()
    },
    UPLOAD_END(state) {
      state.fetchEnd = Date.now()
    },
    UPLOAD_ERROR(state, error) {
      state.error = error
    },
    CHECK_GRADINGS(state) {
      const filtered = state.audits.filter(
        audit =>
          audit.completedAt &&
          audit.planId &&
          audit.sections.length === sectionOrder.length
      )
      /* eslint-disable-next-line */
      const mapped = filtered.map(audit => {
        const submittedSections = audit.sections.slice()
        const inadequate =
          submittedSections.filter(s => s.determination === 'inadequate') || []
        const requiresImprovement =
          submittedSections.filter(
            s => s.determination === 'requires_improvement'
          ) || []
        const good =
          submittedSections.filter(s => s.determination === 'good') || []
        const outstanding =
          submittedSections.filter(s => s.determination === 'outstanding') ||
          []
        const lengths = {
          outstanding: {
            count: outstanding.length,
            sections: outstanding.map(o => o.id)
          },
          good: {
            count: good.length,
            sections: good.map(g => g.id)
          },
          reqImp: {
            count: requiresImprovement.length,
            sections: requiresImprovement.map(ri => ri.id)
          },
          inadequate: {
            count: inadequate.length,
            sections: inadequate.map(i => i.id)
          }
        }
        const numSections = [...Reflect.ownKeys(sections)]
        const canBeGoodCount = ['a', 'b', 'e', 'f'].length
        const canBeOutstandingCount = canBeGoodCount

        let isGood =
          lengths.outstanding.count + lengths.good.count >= 4 &&
          [
            ...lengths.outstanding.sections,
            ...lengths.good.sections
          ].filter(s => s.match(/[abef]/gi)).length >= 4 &&
          lengths.reqImp.count <
            Math.floor((numSections.length - canBeGoodCount) / 2)
        let isOutstanding =
          lengths.outstanding.count >= 4 &&
          lengths.outstanding.sections.filter(s => s.match(/[abef]/gi))
            .length >= 4 &&
          lengths.reqImp.count <
            Math.floor((numSections.length - canBeOutstandingCount) / 2)

        isGood = lengths.inadequate.count > 0 ? false : isGood
        isOutstanding = lengths.inadequate.count > 0 ? false : isOutstanding

        const isInadequate =
          lengths.inadequate.count > 0 &&
          (lengths.inadequate.sections.filter(s => s.match(/[abdf]/gi)).length >
            0 ||
            lengths.inadequate.sections.filter(s => s.match(/[cdgh]/gi))
              .length > 2)

        const isRequiresImprovement =
          !isGood && !isOutstanding && !isInadequate
        const finalGrading = isOutstanding
          ? 'outstanding'
          : isGood
            ? 'good'
            : isRequiresImprovement
              ? 'requires_improvement'
              : 'inadequate'
        audit.outcome = finalGrading
      })
    }
  },
  actions: {
    async fetchAllAssignments({ commit }) {
      commit('FETCH_START')
      try {
        let { data: assignments } = await this.$api.fetchAllAssignments()
        const { data: audits } = await this.$api.getOrganisationAudits()
        assignments = assignments.filter(a => !a.archived)
        commit('SET_ASSIGNMENTS', assignments)
        commit('SET_AUDITS', audits)
        commit('CHECK_GRADINGS')
        commit('FETCH_END')
        return Promise.resolve()
      } catch (err) {
        commit('FETCH_ERROR', { name: 'getPlans', err, date: Date.now() })
        return Promise.reject(err)
      }
    },
    async fetchAssignments({ commit }) {
      commit('FETCH_START')
      try {
        const { data: assignments } = await this.$api.fetchAssignments()
        commit('SET_ASSIGNMENTS', assignments)
        commit('FETCH_END')
        return Promise.resolve()
      } catch (err) {
        commit('FETCH_ERROR', { name: 'getPlans', err, date: Date.now() })
        return Promise.reject(err)
      }
    },
    async createAssignment({ commit }, { planId, userId, deadline }) {
      commit('UPLOAD_START')
      commit('UPLOAD_START')
      try {
        const { data } = await this.$api.createAssignment(
          planId,
          userId,
          deadline
        )
        commit('ADD_ASSIGNMENTS', data)
        commit('UPLOAD_END')
      } catch (err) {
        commit('UPLOAD_ERROR', err)
      }
    },
    setCurrentAssignment({ commit, dispatch }, assignment) {
      commit('SET_CURRENT_ASSIGNMENT', assignment)
      dispatch('plan/setCurrentPlanId', assignment._id, { root: true })
      dispatch('audit/setCurrentAudit', assignment._id, { root: true })
    },
    async setAuditComplete({ commit }, { assignmentId, outcome }) {
      commit('UPLOAD_START')
      try {
        const { data } = await this.$api.setAuditComplete(
          assignmentId,
          outcome
        )
        commit('COMPLETE_ASSIGNMENT', data)
        commit('UPLOAD_END')
      } catch (err) {
        commit('UPLOAD_ERROR', err)
      }
    },
    async archive({ commit }, id) {
      try {
        const { data } = await this.$api.archiveAssignment(id)
        commit('ARCHIVE_ASSIGNMENT', data._id)
      } catch (err) {
        commit('UPLOAD_ERROR', err)
      }
    }
  },
  getters: {
    assignments: state => [...state.assignments],
    audits: state => [...state.audits],
    forUserId: state => userId =>
      [...state.assignments].filter(a => a.userId._id === userId),
    currentAssignment: state => state.currentAssignment
  }
}

export default assignment
